/**
 * RESH — boot and UI behaviour.
 *
 * This is the only RESH script in the critical path, and it is deliberately
 * tiny and dependency-free. Its whole job is to decide *whether* to fetch the
 * Three.js bundle, and to run the handful of non-3D interactions.
 *
 * The 3D bundle is fetched only when all of these are true:
 *   1. the `load` event has fired (so it never competes with LCP),
 *   2. an IntersectionObserver has actually seen the section,
 *   3. the browser can do WebGL at all.
 *
 * If any of them is false, the static fallback that is already painted is the
 * final state, and nothing errors.
 */
(function () {
  'use strict';

  var assets = window.ReshAssets || {};
  var corePromise = null;
  var shaderPromise = null;

  function afterLoad(fn) {
    // Yield once so we stay behind the browser's own post-load work — but via
    // setTimeout, not requestAnimationFrame. rAF does not fire at all while a
    // tab is hidden, so gating setup on it leaves the header, the mobile nav
    // and every IntersectionObserver dead until the tab is first focused.
    // Opening a link in a background tab is common enough that this matters.
    var run = function () {
      setTimeout(fn, 0);
    };
    if (document.readyState === 'complete') {
      run();
    } else {
      window.addEventListener('load', run);
    }
  }

  /**
   * Defer to genuine idle time.
   *
   * Intersection alone is not late enough. Compiling Three.js and building a
   * scene is 1-2s of main-thread work on a throttled mobile CPU, and doing it
   * while the page is still settling lands squarely in the Total Blocking Time
   * window. Waiting for idle moves that cost after the page is interactive:
   * the same work, but no longer paid for by a metric the visitor feels.
   */
  function whenIdle(fn) {
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(fn, { timeout: 3000 });
    } else {
      setTimeout(fn, 300);
    }
  }

  /**
   * Constrained devices — four cores or fewer, or a narrow viewport — skip the
   * purely decorative ambient shaders altogether. A full-screen fragment
   * shader every frame is a poor trade on a phone, and the section is designed
   * to look complete without it.
   */
  function isConstrained() {
    return (navigator.hardwareConcurrency || 8) <= 4 || window.innerWidth < 768;
  }

  function canWebGL() {
    try {
      var canvas = document.createElement('canvas');
      return !!(
        window.WebGLRenderingContext &&
        (canvas.getContext('webgl2') || canvas.getContext('webgl'))
      );
    } catch (e) {
      return false;
    }
  }

  function loadCore() {
    if (!corePromise) {
      if (!assets.core) return Promise.reject(new Error('ReshAssets.core is not set'));
      corePromise = import(assets.core);
    }
    return corePromise;
  }

  function loadShaders() {
    if (!shaderPromise) {
      if (!assets.shaders) return Promise.reject(new Error('ReshAssets.shaders is not set'));
      shaderPromise = import(assets.shaders);
    }
    return shaderPromise;
  }

  /* ---------------------------------------------------------------------
     3D stages
     --------------------------------------------------------------------- */

  var webgl = canWebGL();
  var stageObserver = null;

  function observeStage(el) {
    if (!webgl || !stageObserver || el.getAttribute('data-resh-observed') === 'true') return;
    el.setAttribute('data-resh-observed', 'true');
    stageObserver.observe(el);
  }

  function mountStage(el) {
    loadCore()
      .then(function (mod) {
        return mod.mountStage(el);
      })
      .catch(function (error) {
        // Network failure, blocked module, anything: the fallback stays.
        if (window.console && console.warn) {
          console.warn('[RESH] 3D bundle unavailable; using the static fallback.', error);
        }
      });
  }

  function unmountStage(el) {
    if (!corePromise) return;
    corePromise
      .then(function (mod) {
        mod.unmountStage(el);
      })
      .catch(function () {});
  }

  /* ---------------------------------------------------------------------
     Ambient shader backgrounds
     --------------------------------------------------------------------- */

  var shaderObserver = null;

  function observeShader(el) {
    if (!webgl || !shaderObserver || el.getAttribute('data-resh-observed') === 'true') return;
    if (isConstrained()) return; // decorative only — not worth it on a phone
    el.setAttribute('data-resh-observed', 'true');
    shaderObserver.observe(el);
  }

  function mountShader(el) {
    loadShaders()
      .then(function (mod) {
        return mod.mountShader(el);
      })
      .catch(function () {});
  }

  function unmountShader(el) {
    if (!shaderPromise) return;
    shaderPromise
      .then(function (mod) {
        mod.unmountShader(el);
      })
      .catch(function () {});
  }

  /* ---------------------------------------------------------------------
     Header: transparent over a 3D hero, blush once scrolled
     --------------------------------------------------------------------- */

  function initHeader() {
    var wrap = document.querySelector('[data-resh-header]');
    if (!wrap) return;
    // The theme editor fires section:load constantly. Without this guard every
    // one of those would attach another scroll listener.
    if (wrap.getAttribute('data-resh-header-ready') === 'true') return;
    wrap.setAttribute('data-resh-header-ready', 'true');

    // Transparent over the hero at the top of the page, blush once scrolled.
    //
    // This reads window.scrollY directly rather than observing an injected
    // sentinel element. A sentinel is the fashionable way to do this, but it
    // depends on that element actually being laid out — and an absolutely
    // positioned probe prepended to <body> is very easy to end up measuring as
    // 0×0, at which point the observer reports "not intersecting" at scroll
    // position 0 and the header is solid from the first paint. A passive
    // scroll listener writing one attribute has no such failure mode.
    var scrolled = null;
    var onScroll = function () {
      var next = window.scrollY > 40;
      if (next === scrolled) return; // only touch the DOM on an actual change
      scrolled = next;
      wrap.setAttribute('data-scrolled', String(next));
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });

    var toggle = wrap.querySelector('[data-resh-menu-toggle]');
    var nav = wrap.querySelector('[data-resh-mobile-nav]');
    if (toggle && nav) {
      toggle.addEventListener('click', function () {
        var open = toggle.getAttribute('aria-expanded') === 'true';
        toggle.setAttribute('aria-expanded', String(!open));
        nav.hidden = open;
      });
    }
  }

  /* ---------------------------------------------------------------------
     Wiring
     --------------------------------------------------------------------- */

  function scan(scope) {
    var container = scope || document;
    Array.prototype.forEach.call(
      container.querySelectorAll('[data-resh-stage]'),
      observeStage
    );
    Array.prototype.forEach.call(
      container.querySelectorAll('[data-resh-shader]'),
      observeShader
    );
  }

  function init() {
    initHeader();

    if (!webgl || !('IntersectionObserver' in window)) return;

    stageObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var target = entry.target;
          stageObserver.unobserve(target);
          whenIdle(function () {
            mountStage(target);
          });
        });
      },
      { rootMargin: '200px 0px' }
    );

    shaderObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var target = entry.target;
          shaderObserver.unobserve(target);
          whenIdle(function () {
            mountShader(target);
          });
        });
      },
      { rootMargin: '200px 0px' }
    );

    scan(document);
  }

  afterLoad(init);

  // The theme editor adds and removes sections constantly. Mount on load,
  // and — critically — release GPU resources on unload.
  document.addEventListener('shopify:section:load', function (event) {
    initHeader();
    scan(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var target = event.target;
    Array.prototype.forEach.call(target.querySelectorAll('[data-resh-stage]'), unmountStage);
    Array.prototype.forEach.call(target.querySelectorAll('[data-resh-shader]'), unmountShader);
  });
})();
